import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, Hammer, Inbox, Plus, TrendingUp, UserX } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { BarList } from '../../components/charts';
import { useToast } from '../../components/toast';
import { PriorityBadge } from '../../components/work-order-badges';
import { WorkOrderFormModal } from '../../components/WorkOrderFormModal';
import { Alert, Badge, Button, Card, CardHeader, cn, EmptyState, ErrorState, PageHeader, PageLoader, Segmented, Select, Spinner, StatCard, Table, Td, Th } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useAssignees } from '../../lib/automation';
import { useDesk } from '../../lib/desk';
import { equipmentLabel, placeLabel, type FacilitiesDashboard } from '../../lib/facilities';
import { formatRelative } from '../../lib/format';
import type { Page, WorkOrder } from '../../lib/types';

type QueueView = 'unassigned' | 'overdue' | 'urgent' | 'open';

const QUEUE_QUERY: Record<QueueView, Record<string, string | boolean>> = {
  unassigned: { assignedTo: 'unassigned' },
  overdue: { overdue: true },
  urgent: { priority: 'HIGH,URGENT' },
  open: {},
};

const QUEUE_LINK: Record<QueueView, string> = {
  unassigned: '/facilities/work-orders?view=unassigned',
  overdue: '/facilities/work-orders?view=overdue',
  urgent: '/facilities/work-orders?priority=HIGH,URGENT',
  open: '/facilities/work-orders',
};

/** The whole facilities queue at a glance: who's carrying what, what's due, and where work is backing up. */
export function FacilitiesDashboardPage() {
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [queueView, setQueueView] = useState<QueueView>('unassigned');
  const [requesting, setRequesting] = useState(false);
  const canAssign = desk.can('TECHNICIAN');

  const dashboard = useQuery({
    queryKey: ['facilities', 'dashboard'],
    queryFn: () => api.get<FacilitiesDashboard>('/facilities/dashboard'),
    refetchInterval: 30_000,
  });
  const queue = useQuery({
    queryKey: ['work-orders', 'list', desk.service, 'dashboard', queueView],
    queryFn: () => api.get<Page<WorkOrder>>('/work-orders', desk.query({ state: 'open', pageSize: 20, ...QUEUE_QUERY[queueView] })),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const assignees = useAssignees(canAssign);

  const reassign = useMutation({
    mutationFn: ({ id, assignedToId }: { id: string; number: number; assignedToId: string | null }) => api.patch(`/work-orders/${id}`, { assignedToId }),
    onSuccess: (_result, { number, assignedToId }) => {
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      const name = assignees.data?.find((person) => person.id === assignedToId)?.name;
      toast.success(name ? `Assigned #${number} to ${name}` : `#${number} is unassigned`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (dashboard.isLoading) return <PageLoader />;
  if (dashboard.error || !dashboard.data) return <ErrorState error={dashboard.error} onRetry={() => dashboard.refetch()} />;

  const { totals, maintenance, technicians, bottlenecks } = dashboard.data;
  const busiest = Math.max(1, ...technicians.map((tech) => tech.open));
  const items = queue.data?.items ?? [];

  const maintenanceRows = [
    {
      label: 'Preventive maintenance open',
      value: maintenance.preventiveOpen,
      sub: maintenance.preventiveOverdue ? `${maintenance.preventiveOverdue} overdue` : 'None overdue',
      alert: maintenance.preventiveOverdue > 0,
      to: '/facilities/maintenance',
    },
    { label: 'Schedules due in the next 2 weeks', value: maintenance.schedulesDueSoon, sub: null, alert: false, to: '/facilities/maintenance?tab=schedules' },
    {
      label: 'Inspections open',
      value: maintenance.inspectionsOpen,
      sub: [maintenance.inspectionsDueToday && `${maintenance.inspectionsDueToday} due today`, maintenance.inspectionsOverdue && `${maintenance.inspectionsOverdue} overdue`]
        .filter(Boolean)
        .join(' · ') || null,
      alert: maintenance.inspectionsOverdue > 0,
      to: '/facilities/inspections',
    },
    { label: 'Inspection follow-ups open', value: maintenance.followUpsOpen, sub: null, alert: false, to: '/facilities/work-orders' },
    {
      label: 'Inspections with failures (30 days)',
      value: maintenance.failedInspections30Days,
      sub: null,
      alert: false,
      to: '/facilities/inspections?state=completed',
    },
  ];

  return (
    <>
      <PageHeader
        title="Facilities"
        description="The maintenance queue across every building: who’s carrying what, what’s due, and where work is backing up."
        actions={
          <>
            <Link to="/facilities/work-orders">
              <Button>All work orders</Button>
            </Link>
            <Button variant="primary" icon={<Plus />} onClick={() => setRequesting(true)}>
              {canAssign ? 'New work order' : 'Request work'}
            </Button>
          </>
        }
      />

      {bottlenecks.length > 0 ? (
        <Alert tone="amber" icon={<AlertTriangle />} title="Where work is backing up" className="mb-6">
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {bottlenecks.map((bottleneck) => (
              <li key={`${bottleneck.kind}-${bottleneck.id}-${bottleneck.message}`}>{bottleneck.message}</li>
            ))}
          </ul>
        </Alert>
      ) : (
        totals.open > 0 && (
          <Alert tone="green" icon={<CheckCircle2 />} className="mb-6">
            No bottlenecks right now. Work is spread evenly and nothing has been waiting too long.
          </Alert>
        )
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Open" value={totals.open} sub={`${totals.inProgress} in progress · ${totals.waiting} waiting`} icon={<Inbox />} to="/facilities/work-orders" />
        <StatCard label="Unassigned" value={totals.unassigned} icon={<UserX />} tone={totals.unassigned ? 'amber' : 'green'} to="/facilities/work-orders?view=unassigned" />
        <StatCard label="Overdue" value={totals.overdue} icon={<Clock />} tone={totals.overdue ? 'red' : 'green'} to="/facilities/work-orders?view=overdue" />
        <StatCard label="Due today" value={totals.dueToday} icon={<CalendarClock />} tone="violet" to="/facilities/maintenance" />
        <StatCard label="High or urgent" value={totals.urgent} icon={<Hammer />} tone={totals.urgent ? 'red' : 'brand'} to="/facilities/work-orders?priority=HIGH,URGENT" />
        <StatCard label="This week" value={`${totals.resolvedThisWeek} done`} sub={`${totals.createdThisWeek} new`} icon={<TrendingUp />} tone="green" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Queue"
              description={canAssign ? 'Open work across every building. Reassign it right here.' : 'Open work across every building.'}
              actions={
                <Link to={QUEUE_LINK[queueView]} className="text-sm font-medium text-brand-700 hover:underline">
                  See all
                </Link>
              }
            />
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 pb-3">
              <Segmented<QueueView>
                value={queueView}
                onChange={setQueueView}
                options={[
                  { value: 'unassigned', label: `Unassigned (${totals.unassigned})` },
                  { value: 'overdue', label: `Overdue (${totals.overdue})` },
                  { value: 'urgent', label: `High & urgent (${totals.urgent})` },
                  { value: 'open', label: 'All open' },
                ]}
              />
              {queue.isFetching && !queue.isLoading && <Spinner className="size-4" />}
            </div>
            {queue.isLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner />
              </div>
            ) : queue.error ? (
              <ErrorState error={queue.error} onRetry={() => queue.refetch()} />
            ) : items.length === 0 ? (
              <EmptyState icon={<CheckCircle2 />} title="Nothing here" description="This part of the queue is clear." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Work order</Th>
                    <Th className="hidden md:table-cell">Where</Th>
                    <Th>Priority</Th>
                    <Th className="hidden sm:table-cell">Opened</Th>
                    <Th>Assigned to</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {items.map((ticket) => {
                    const overdue = !!ticket.dueAt && new Date(ticket.dueAt) < new Date();
                    const options = [...(assignees.data ?? [])];
                    if (ticket.assignedTo && !options.some((person) => person.id === ticket.assignedTo!.id)) {
                      options.push({ id: ticket.assignedTo.id, name: ticket.assignedTo.name, email: '' });
                    }
                    return (
                      <tr key={ticket.id}>
                        <Td className="max-w-[16rem]">
                          <Link to={desk.workOrderHref(ticket.id)} className="block truncate font-medium text-slate-900 hover:text-brand-700">
                            <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                          </Link>
                          <p className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                            {ticket.department?.name}
                            {overdue && <Badge tone="red">Overdue</Badge>}
                          </p>
                        </Td>
                        <Td className="hidden max-w-[14rem] md:table-cell">
                          <p className="truncate text-slate-600">
                            {placeLabel({ building: ticket.building ?? ticket.room?.building, room: ticket.room, locationDetail: ticket.locationDetail }) || '—'}
                          </p>
                          {ticket.facilityAsset && <p className="truncate text-xs text-slate-500">{equipmentLabel(ticket.facilityAsset)}</p>}
                        </Td>
                        <Td>
                          <PriorityBadge priority={ticket.priority} />
                        </Td>
                        <Td className="hidden whitespace-nowrap text-slate-500 sm:table-cell">{formatRelative(ticket.createdAt)}</Td>
                        <Td className="min-w-[10rem]">
                          {canAssign ? (
                            <Select
                              value={ticket.assignedToId ?? ''}
                              disabled={reassign.isPending}
                              aria-label={`Assign work order #${ticket.number}`}
                              onChange={(event) => reassign.mutate({ id: ticket.id, number: ticket.number, assignedToId: event.target.value || null })}
                            >
                              <option value="">Unassigned</option>
                              {options.map((person) => (
                                <option key={person.id} value={person.id}>
                                  {person.name}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <span className="text-slate-600">{ticket.assignedTo?.name ?? 'Unassigned'}</span>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader title="By building" description="Open work orders" />
              <div className="p-5">
                <BarList
                  emptyText="No open work"
                  items={dashboard.data.buildings.map((row) => ({
                    key: row.buildingId ?? 'none',
                    label: row.name,
                    value: row.open,
                    secondary: row.overdue ? `${row.overdue} overdue` : undefined,
                    tone: row.overdue ? 'red' : 'brand',
                    to: row.buildingId ? `/facilities/work-orders?buildingId=${row.buildingId}` : undefined,
                  }))}
                />
              </div>
            </Card>
            <Card>
              <CardHeader title="By category" description="Open work orders" />
              <div className="p-5">
                <BarList
                  emptyText="No open work"
                  items={dashboard.data.categories.map((row) => ({
                    key: row.departmentId ?? 'none',
                    label: row.path,
                    value: row.open,
                    secondary: row.unassigned ? `${row.unassigned} unassigned` : undefined,
                    tone: row.unassigned >= 3 ? 'amber' : 'brand',
                    to: `/facilities/work-orders?departmentId=${row.departmentId ?? 'none'}`,
                  }))}
                />
              </div>
            </Card>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Who’s carrying what" description="Open work by person. Select someone to see their queue." />
            {technicians.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">
                Nobody has a facilities role yet. A school administrator can give people Facilities technician access under Settings › Users &amp; roles.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {technicians.map((tech) => (
                  <li key={tech.userId}>
                    <Link to={`/facilities/work-orders?view=all&assignee=${tech.userId}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
                          <span className="truncate">{tech.name}</span>
                          {tech.away && <Badge tone="amber">Away</Badge>}
                        </p>
                        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div className={cn('h-full rounded-full', tech.overdue ? 'bg-red-500' : 'bg-brand-500')} style={{ width: `${(tech.open / busiest) * 100}%` }} />
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {[
                            `${tech.inProgress} in progress`,
                            tech.overdue > 0 && `${tech.overdue} overdue`,
                            tech.urgent > 0 && `${tech.urgent} high or urgent`,
                            tech.oldestOpenDays !== null && tech.oldestOpenDays > 0 && `oldest ${tech.oldestOpenDays}d`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                      <span className="text-lg font-semibold text-slate-900 tabular-nums">{tech.open}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader title="Maintenance and inspections" />
            <ul className="divide-y divide-slate-100">
              {maintenanceRows.map((row) => (
                <li key={row.label}>
                  <Link to={row.to} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-700">{row.label}</p>
                      {row.sub && <p className={cn('text-xs', row.alert ? 'font-medium text-red-600' : 'text-slate-500')}>{row.sub}</p>}
                    </div>
                    <span className="text-lg font-semibold text-slate-900 tabular-nums">{row.value}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <CardHeader title="How long work has waited" description="Open work orders by age" />
            <div className="p-5">
              <BarList
                items={dashboard.data.aging.map((row, index) => ({
                  key: row.label,
                  label: row.label,
                  value: row.count,
                  tone: index >= 3 && row.count > 0 ? 'red' : index === 2 && row.count > 0 ? 'amber' : 'brand',
                }))}
              />
            </div>
          </Card>
        </div>
      </div>

      <WorkOrderFormModal open={requesting} onClose={() => setRequesting(false)} staff={canAssign} />
    </>
  );
}
